/**
 * use-crop-form-data
 *
 * Bundles all reference data fetching for the crop modal into a single hook.
 * Returns data needed to populate selects, autocompletes, and the org hierarchy.
 */

import { useMemo } from 'react'
import { useCropGrowthTypes } from './use-growth-types'
import { useCropLifecycleTypes } from './use-lifecycle-types'
import { useCropPropagationYypes } from './use-propagation-types'
import { useCollectionTypes } from './use-collection-types'
import { useCropArchetypes } from './use-crop-archetypes'
import { useCropFamilyTypes } from './use-crop-family-types'
import { useFarmChildren } from './use-farm-children'
import { useClimate } from '~/crop/crop-schedule/hooks/use-climate'
import { useOrgContext } from '~/common/context/organization/org-context'
import { useOrgParams } from '~/common/use-org-params'
import { useCropModalContext } from '../context/crop-modal-context'
import { useCropTemplates } from './use-crop-templates'

export const useCropFormData = () => {
  const { selectedCollections } = useCropModalContext()

  const { farmId } = useOrgParams()
  const { activeOrgs } = useOrgContext()

  const { cropGrowthTypes } = useCropGrowthTypes()
  const { cropLifecycleTypes } = useCropLifecycleTypes()
  const { cropPropagationYypes } = useCropPropagationYypes()
  const { collectionTypes } = useCollectionTypes()
  const { cropTemplates } = useCropTemplates({ collectionCodes: selectedCollections })
  const { cropArchetypes } = useCropArchetypes()
  const { cropFamilyTypes } = useCropFamilyTypes()
  const { childOrgs, invalidate } = useFarmChildren({ type: 'patch' })
  const { climateData } = useClimate({ farmId })

  // Default orgs to add to
  const [farmName = '', plotName = '', patchName = ''] = activeOrgs.map(org => org.name)

  // Transform flat childOrgs into hierarchical structure for org dropdown
  const hierarchalOrgs = useMemo(() => {
    const plots = childOrgs.filter(org => org.organization_type === 'plot')
    return [
      activeOrgs.find(org => org.organization_type === 'farm'),
      ...plots.map(plot => ({
        ...plot,
        patches: childOrgs.filter(org =>
          org.organization_type === 'patch' &&
          org.parent_organization_id === plot.id
        )
      }))
    ]
  }, [childOrgs, activeOrgs])

  return {
    cropGrowthTypes,
    cropLifecycleTypes,
    cropPropagationYypes,
    collectionTypes,
    cropTemplates,
    cropArchetypes,
    cropFamilyTypes,
    childOrgs,
    climateData,
    activeOrgs,
    farmName,
    plotName,
    patchName,
    hierarchalOrgs,
    invalidate,
  }
}
